import React, { useState, useEffect } from "react";
import PropTypes from "prop-types";
import { useTranslation } from "react-i18next";
import { bindActionCreators } from "redux";
import { Grid, Divider, Checkbox } from "@mui/material";
import { TextField, Autocomplete, IconButton } from "@totalsoft/rocket-ui";
import CancelIcon from "@mui/icons-material/Cancel";
import { onCheckBoxChange } from "utils/propertyChangeAdapters";
import { loadCounties, loadCities } from "../../actionCreators";
import { isValid, getErrors } from "@totalsoft/pure-validations-react";
import { connect } from "react-redux";

const AddressContainer = ({
  item,
  countries,
  addressTypes,
  onPartnerAddressPropertyChange,
  actions,
  deleteAddress,
  validationAddressSelection,
  hasWritePermission,
  idx
}) => {
  const { t } = useTranslation();

  const [localCounties, setLocalCounties] = useState([]);
  const [localCities, setLocalCities] = useState([]);

  const onPropertyChange = prop => value => {
    if (prop === "countryId") {
      onPartnerAddressPropertyChange("countryId", "countryName")(value.countryId, value.countryName);
      return;
    }

    if (prop === "districtId") {
      onPartnerAddressPropertyChange("districtId", "districtName")(value.districtId, value.districtName);
      actions.loadCities(value.districtId).then(data => {
        setLocalCities(data ? Object.values(data) : []);
      });

      return;
    }

    if (prop === "cityId") {
      onPartnerAddressPropertyChange("cityId", "cityName")(value.cityId, value.cityName);
      return;
    }

    if (prop === "isActive" || prop === "isDefault") {
      if (item.addressTypeId && addressTypes.find(x => x.addressTypeId === item.addressTypeId)?.isDefault) {
        onPartnerAddressPropertyChange("isActive", "isDefault")(value, value);
        return;
      }
    }

    onPartnerAddressPropertyChange(prop)(value);
  };

  useEffect(() => {
    if (item.countryId) {
      actions.loadCounties(item.countryId).then(data => {
        setLocalCounties(data ? Object.values(data) : []);
      });
    }
    if (item.districtId) {
      actions.loadCities(item.districtId).then(data => {
        setLocalCities(data ? Object.values(data) : []);
      });
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (item.countryId) {
      actions.loadCounties(item.countryId).then(data => {
        setLocalCounties(data ? Object.values(data) : []);
      });
    }
    if (item.districtId) {
      actions.loadCities(item.districtId).then(data => {
        setLocalCities(data ? Object.values(data) : []);
      });
    }

    if (item.districtId === null) {
      setLocalCities([]);
    }
  }, [item.countryId, item.districtId]); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <Grid container spacing={2} key={item.partnerAddressId} style={{ paddingTop: "25px" }}>
      <>
        <Grid item xs={12} md={4} sm={6}>
          <Autocomplete
            fullWidth
            required
            label={t("PartnerAddress.Type")}
            id={"address_" + idx + "_addressType"}
            value={addressTypes.length > 0 ? item.addressTypeId || "" : ""}
            valueKey="addressTypeId"
            labelKey="addressTypeName"
            options={addressTypes}
            simpleValue={true}
            onChange={onPartnerAddressPropertyChange("addressTypeId")}
            disabled={!hasWritePermission}
            error={!isValid(validationAddressSelection[idx].addressTypeId)}
            helperText={getErrors(validationAddressSelection[idx].addressTypeId)}
          />
        </Grid>

        <Grid item xs={12} md={4} sm={6}>
          <Autocomplete
            fullWidth
            required
            label={t("PartnerAddress.Country")}
            id={"address_" + idx + "_country"}
            value={item}
            valueKey="countryId"
            labelKey="countryName"
            options={countries}
            onChange={onPropertyChange("countryId")}
            disabled={!hasWritePermission}
            error={!isValid(validationAddressSelection[idx].countryId)}
            helperText={getErrors(validationAddressSelection[idx].countryId)}
          />
        </Grid>

        <Grid item xs={12} md={4} sm={6}>
          <Autocomplete
            fullWidth
            required
            label={t("PartnerAddress.County")}
            id={"address_" + idx + "_county"}
            value={item}
            valueKey="districtId"
            labelKey="districtName"
            options={localCounties}
            onChange={onPropertyChange("districtId")}
            disabled={!hasWritePermission}
            error={!isValid(validationAddressSelection[idx].districtId)}
            helperText={getErrors(validationAddressSelection[idx].districtId)}
          />
        </Grid>

        <Grid item xs={12} md={4} sm={6}>
          <Autocomplete
            fullWidth
            required
            label={t("PartnerAddress.City")}
            id={"address_" + idx + "_city"}
            value={item}
            valueKey="cityId"
            labelKey="cityName"
            options={localCities}
            onChange={onPropertyChange("cityId")}
            disabled={!hasWritePermission}
            error={!isValid(validationAddressSelection[idx].cityId)}
            helperText={getErrors(validationAddressSelection[idx].cityId)}
          />
        </Grid>

        <Grid item xs={12} md={4} sm={6} style={{ paddingTop: "15px" }}>
          <TextField
            id={"address_" + idx + "_street"}
            fullWidth
            value={item.street || ""}
            name="role"
            label={t("PartnerAddress.Street")}
            onChange={onPropertyChange("street")}
            disabled={!hasWritePermission}
            error={!isValid(validationAddressSelection[idx].street)}
            helperText={getErrors(validationAddressSelection[idx].street)}
          />
        </Grid>

        <Grid item xs={12} md={4} sm={6} style={{ paddingTop: "15px" }}>
          <TextField
            id={"address_" + idx + "_streetNumber"}
            fullWidth
            value={item.streetNumber || ""}
            label={t("PartnerAddress.Number")}
            onChange={onPropertyChange("streetNumber")}
            disabled={!hasWritePermission}
            error={!isValid(validationAddressSelection[idx].streetNumber)}
            helperText={getErrors(validationAddressSelection[idx].streetNumber)}
          />
        </Grid>

        <Grid item xs={12} md={4} sm={6} style={{ paddingTop: "15px" }}>
          <TextField
            id={"address_" + idx + "_buildingNumber"}
            fullWidth
            value={item.buildingNumber || ""}
            label={t("PartnerAddress.Building")}
            onChange={onPropertyChange("buildingNumber")}
            disabled={!hasWritePermission}
            error={!isValid(validationAddressSelection[idx].buildingNumber)}
            helperText={getErrors(validationAddressSelection[idx].buildingNumber)}
          />
        </Grid>

        <Grid item xs={12} md={4} sm={6} style={{ paddingTop: "15px" }}>
          <TextField
            id={"address_" + idx + "_stairNumber"}
            fullWidth
            value={item.stairNumber || ""}
            label={t("PartnerAddress.Stair")}
            onChange={onPropertyChange("stairNumber")}
            disabled={!hasWritePermission}
            error={!isValid(validationAddressSelection[idx].stairNumber)}
            helperText={getErrors(validationAddressSelection[idx].stairNumber)}
          />
        </Grid>

        <Grid item xs={12} md={4} sm={6} style={{ paddingTop: "15px" }}>
          <TextField
            id={"address_" + idx + "_flatNumber"}
            fullWidth
            value={item.flatNumber || ""}
            label={t("PartnerAddress.FlatNumber")}
            onChange={onPropertyChange("flatNumber")}
            disabled={!hasWritePermission}
            error={!isValid(validationAddressSelection[idx].flatNumber)}
            helperText={getErrors(validationAddressSelection[idx].flatNumber)}
          />
        </Grid>

        <Grid item xs={12} md={4} sm={6} style={{ paddingTop: "15px" }}>
          <TextField
            id={"address_" + idx + "_floorNumber"}
            fullWidth
            value={item.floorNumber || ""}
            label={t("PartnerAddress.Floor")}
            onChange={onPropertyChange("floorNumber")}
            disabled={!hasWritePermission}
            error={!isValid(validationAddressSelection[idx].floorNumber)}
            helperText={getErrors(validationAddressSelection[idx].floorNumber)}
          />
        </Grid>

        <Grid item xs={12} md={4} sm={6} style={{ paddingTop: "15px" }}>
          <TextField
            id={"address_" + idx + "_zipCode"}
            fullWidth
            value={item.zipCode || ""}
            label={t("PartnerAddress.ZipCode")}
            onChange={onPropertyChange("zipCode")}
            disabled={!hasWritePermission}
            error={!isValid(validationAddressSelection[idx].zipCode)}
            helperText={getErrors(validationAddressSelection[idx].zipCode)}
          />
        </Grid>

        <Grid item xs={12} md={4} sm={6} style={{ paddingTop: "15px" }}>
          <TextField
            id={"address_" + idx + "_neighborhood"}
            fullWidth
            value={item.neighborhood || ""}
            label={t("PartnerAddress.Neighborhood")}
            onChange={onPropertyChange("neighborhood")}
            disabled={!hasWritePermission}
            error={!isValid(validationAddressSelection[idx].neighborhood)}
            helperText={getErrors(validationAddressSelection[idx].neighborhood)}
          />
        </Grid>

        <Grid item xs={12} sm={4}>
          <span> {t("PartnerAddress.IsActive")}</span>:{" "}
          <Checkbox
            id={"address_" + idx + "_isActive"}
            checked={item.isActive || false}
            onChange={onCheckBoxChange(onPropertyChange("isActive"))}
            disabled={!hasWritePermission}
          />
        </Grid>

        <Grid item xs={12} sm={4}>
          <span> {t("PartnerAddress.IsDefault")}</span>:{" "}
          <Checkbox
            id={"address_" + idx + "_isDefault"}
            checked={item.isDefault || false}
            onChange={onCheckBoxChange(onPropertyChange("isDefault"))}
            disabled={!hasWritePermission}
          />
        </Grid>
        <Grid item xs={12} sm={4} sx={{ textAlign: "end" }}>
          <IconButton
            id={"address_" + idx + "_deleteBtn"}
            color="primary"
            variant="text"
            onClick={() => deleteAddress(item)}
            disabled={!hasWritePermission}
          >
            <CancelIcon />
          </IconButton>
        </Grid>
        <Grid item xs={12} md={12} sm={12}>
          <Divider pb="1" />
        </Grid>
      </>
    </Grid>
  );
};

AddressContainer.propTypes = {
  item: PropTypes.object.isRequired,
  countries: PropTypes.array.isRequired,
  addressTypes: PropTypes.array.isRequired,
  onPartnerAddressPropertyChange: PropTypes.func.isRequired,
  actions: PropTypes.object.isRequired,
  deleteAddress: PropTypes.func.isRequired,
  validationAddressSelection: PropTypes.object.isRequired,
  idx: PropTypes.number.isRequired,
  hasWritePermission: PropTypes.bool.isRequired
};

function mapStateToProps() {
  return {};
}

function mapDispatchToProps(dispatch) {
  return {
    actions: bindActionCreators(
      {
        loadCounties,
        loadCities
      },
      dispatch
    ),
    dispatch
  };
}

export default connect(mapStateToProps, mapDispatchToProps)(AddressContainer);
